import { ReactElement, useEffect, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { keepPreviousData } from '@tanstack/react-query';
import { MenuProps } from 'antd';
import Form, { useForm } from 'antd/es/form/Form';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { FormItem } from 'common/components/antd/Form/FormItem';
import { Input } from 'common/components/antd/Input/Input';
import { Select } from 'common/components/antd/Select/Select';
import { Space } from 'common/components/antd/Space/Space';
import { Table } from 'common/components/antd/Table/Table';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { PERSISTENCE_KEYS } from 'common/constants/persistence.constants';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { EEmissionAccountColumnKeys } from 'domains/accounts/constants/accounts.constants';
import { useCounterpartyAccountsListQuery } from 'domains/accounts/queries/accounts.query';
import { selectedCounterpartyAtom } from 'domains/accounts/store/selected-counterparty';
import { EAccountKind, TAccountsListResData } from 'domains/accounts/types/accounts.types';
import { makeAccountFilterByGUIDOrIBAN } from 'domains/accounts/utils/acc-iban-guid';
import { TCounterpartiesListResData } from 'domains/counterparties/types/counterparty.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import {
  SDangerActionItem,
  SFiltersContainer,
} from './counterparty-emission.page.style';
import {
  EAccountManagementCounterpartyEmissionPageGetParams,
  EAccountManagementCounterpartyEmissionPageGetParamsActionTypes,
  ECounterpartyEmissionSearchForm,
  EModalType,
  ERowAction,
} from './enums/counerparty-emisson.enums';
import CreateEditEmissionAccountModal from './modals/create-edit-emission.modal';
import { getColumns } from './table/counterparty-emission.columns';
import {
  TAccountManagementCounterpartyEmissionPageLoaderData, TCustomersModalData,
  TEmissionAccountRecord,
  TItemClickEventHandler,
} from './types/counterparty-emission.types';

type TPaginationConfig = {
  page: number;
  pageSize: number;
};

const AccountManagementCounterpartyEmissionPage = (): ReactElement => {
  const navigate = useNavigate();
  const [form] = useForm();
  const loaderData = useLoaderData() as TAccountManagementCounterpartyEmissionPageLoaderData;
  const { t } = useTranslation();
  const [paginationData, setPaginationData] = useState<TPaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [selectedCounterparty, setSelectedCounterparty] = useAtom(selectedCounterpartyAtom);
  const [selectedAccountNumberIban, setSelectedAccountNumberIban] = useState({});
  const [modalData, setModalData] = useState<TCustomersModalData | {}>({});

  useEffect(() => {
    if (!selectedCounterparty) {
      setSelectedCounterparty(IS_ADMIN_FE ? loaderData.defaultSelectedCounterparty : ENV_COUNTERPARTY_GUID);
    }

    form.setFields([
      {
        name: ECounterpartyEmissionSearchForm.CounterpartyName,
        value: IS_ADMIN_FE ? loaderData.defaultSelectedCounterparty : ENV_COUNTERPARTY_GUID,
      },
    ]);
  }, []);

  const {
    data: counterpartiesAccountsData,
    isLoading: isCounterpartiesAccountsDataLoading,
    isError: isCounterpartiesAccountsDataError,
    error: counterpartiesAccountsDataError,
  } = useCounterpartyAccountsListQuery(
    selectedCounterparty,
    {
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
      account_kinds: [EAccountKind.Emission],
      ...selectedAccountNumberIban,
    },
    {
      placeholderData: keepPreviousData,
      refetchOnMount: false,
      enabled: !!selectedCounterparty,
    },
  );

  const isRequestsError = isCounterpartiesAccountsDataError;
  const requestError = counterpartiesAccountsDataError;

  useEffect(() => {
    if (isRequestsError) {
      console.error(requestError);
    }
  }, [isRequestsError, requestError]);

  const counterpartyOptions = (loaderData?.counterpartiesListData?.counterparties || [])
    .map((counterparty) => ({
      label: counterparty.name,
      value: counterparty.guid,
    }))
    .filter(counterparty => IS_ADMIN_FE
    ? counterparty.value !== ENV_COUNTERPARTY_GUID
    : counterparty.value === ENV_COUNTERPARTY_GUID
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(document.location.search);
    const pageAction = searchParams.get(EAccountManagementCounterpartyEmissionPageGetParams.Action);

    if (pageAction === EAccountManagementCounterpartyEmissionPageGetParamsActionTypes.Create) {
      setModalData({
        type: EModalType.Create,
        rowData: {
          counterpartyOptions,
          selectedCounterparty: IS_ADMIN_FE ? selectedCounterparty : ENV_COUNTERPARTY_GUID,
        },
      });
    }
  }, [document.location.search]);

  const tableData: TEmissionAccountRecord[] = (counterpartiesAccountsData?.accounts || []).map((item, index) => ({
    ...item,
    // id: offset + index + 1, // TODO: need to fix
    id: index + 1, // TODO: need to fix
    currency: 'UAH', // mocks
    account_name: 'Emission accounts', // mocks
    actual_balance: '50 000', // mocks
    issue_burn_saldo: '39 912', // mocks
    reserve_acc: '1 543 217', // mocks
    account_status: 1, // mocks
  }));

  const actions: MenuProps['items'] = [
    {
      key: ERowAction.Edit,
      label: t('accountManagement.counterpartyEmission.table.menu.action.edit'),
    },
    {
      key: ERowAction.Delete,
      label:
        <SDangerActionItem>
        {t('accountManagement.counterpartyEmission.table.menu.action.delete')}
        </SDangerActionItem>,
    },
  ];

  const onItemActionClick: TItemClickEventHandler = ({ item }) => {
    const fn: MenuProps['onClick'] = ({ key }) => {
      switch (key) {
        case ERowAction.Edit: {
          setModalData({
            type: EModalType.Edit,
            rowData: {
              counterpartyOptions,
              selectedCounterparty,
              record: item,
            },
          });
          break;
        }
        case ERowAction.Delete: {
          throw new Error('Deletion is not implemented');
          break;
        }
        default: {
          throw new Error(`click handler for menu item with key [${key}] is not implemented`);
        }
      }
    };
    return fn;
  };

  const columns = getColumns({
    menuItems: actions,
    onItemActionClick,
  });

  const onPaginationChangeHandler = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const paginationConfig = {
    style: {
      marginRight: 30,
    },
    showQuickJumper: true,
    showSizeChanger: true,
    total: counterpartiesAccountsData?.total,
    pageSize: paginationData.pageSize,
    current: paginationData.page,
    onChange: onPaginationChangeHandler,
  };

  const onCancelModalHandler = (): void => {
    setModalData({});
    navigate(ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_COUNTERPARTY_EMISSION);
  };

  const onFinishFormHandler = async (values: Record<ECounterpartyEmissionSearchForm, string>): Promise<void> => {
    const { counterpartyName, wallet_account_guids } = values;

    if (counterpartyName) {
      setSelectedCounterparty(counterpartyName);
    }

    if (wallet_account_guids !== undefined) {
      const { filter } = makeAccountFilterByGUIDOrIBAN(wallet_account_guids);
      if (filter.field) {
        setSelectedAccountNumberIban({ [filter.field]: filter.value });
      } else {
        setSelectedAccountNumberIban({});
      }
    }
  };

  return (
    <div>
      <SFiltersContainer>
        <Form
          form={form}
          layout="inline"
          onFinish={onFinishFormHandler}
        >
          <Space>
            {IS_ADMIN_FE && (
              <FormItem
                name={ECounterpartyEmissionSearchForm.CounterpartyName}
              >
                <Select
                  style={{
                    width: 272,
                  }}
                  options={counterpartyOptions}
                />
              </FormItem>
            )}
            <FormItem
              name={ECounterpartyEmissionSearchForm.WalletAccountGUID}
              rules={[
                (): { validator(_: any, value: any): Promise<any> } => ({
                  validator(_, value): Promise<any> {
                    if (value !== undefined && value.length > 0) {
                      const { error } = makeAccountFilterByGUIDOrIBAN(value);

                      if (error) {
                        return Promise.reject(new Error(error));
                      }
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input
                placeholder={t('accountManagement.counterpartyEmission.search.accountNumberOrIban.placeholder')!}
                allowClear
                suffix={<SearchOutlined />}
                minLength={2}
                rootClassName="iban-input"
              />
            </FormItem>
            <Button
              loading={isCounterpartiesAccountsDataLoading}
              htmlType="submit"
              type="primary"
            >
              {t('accountManagement.counterpartyEmission.button.search')}
            </Button>
          </Space>
        </Form>
      </SFiltersContainer>
      <Table
        loading={isCounterpartiesAccountsDataLoading}
        pagination={paginationConfig}
        rowKey={EEmissionAccountColumnKeys.ID}
        dataSource={tableData}
        columns={columns}
      />
      <CreateEditEmissionAccountModal
        isOpen={!!Object.keys(modalData).length}
        modalData={modalData as TCustomersModalData}
        onCancelModalHandler={onCancelModalHandler}
      />
    </div>
  );
};

export const loader = async (): Promise<{
  defaultSelectedCounterparty: string,
  counterpartiesListData: TCounterpartiesListResData | {},
  counterpartyAccountsData: TAccountsListResData | {},
}> => {
  try {
    const counterpartiesListQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList();
    const counterpartiesListData: TCounterpartiesListResData =
      queryClient.getQueryData(counterpartiesListQuery.queryKey) ??
      (await queryClient.fetchQuery(counterpartiesListQuery));

    const filteredCounterpartyList = (counterpartiesListData as TCounterpartiesListResData).counterparties
      .filter(({ guid }) => guid !== ENV_COUNTERPARTY_GUID);

    const selectCounterpartyFromLocalStore = localStorage.getItem(
      PERSISTENCE_KEYS.ACCOUNT_MANAGEMENT_SELECTED_COUNTERPARTY
    );

    const checkedCounterpartyGuid =
      filteredCounterpartyList.find(item => item.guid === selectCounterpartyFromLocalStore)?.guid;

    const defaultSelectedCounterparty = checkedCounterpartyGuid
      || filteredCounterpartyList[filteredCounterpartyList.length - 1].guid;

    const counterpartyAccountsQuery = queryKeysClient[EQueryKeys.AccountQueryKeys].getCounterpartyAccounts(
      defaultSelectedCounterparty,
      {
        offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
        limit: DEFAULT_PAGE_SIZE,
        account_kinds: [EAccountKind.Emission],
      }
    );
    const counterpartyAccountsData: TAccountsListResData =
      queryClient.getQueryData(counterpartyAccountsQuery.queryKey) ??
      (await queryClient.fetchQuery(counterpartyAccountsQuery));

    return {
      counterpartiesListData,
      defaultSelectedCounterparty,
      counterpartyAccountsData,
    };
  } catch (error) {
    console.error(error);

    return {
      counterpartiesListData: {},
      defaultSelectedCounterparty: '',
      counterpartyAccountsData: {},
    };
  }
};

export default AccountManagementCounterpartyEmissionPage;
